import { ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { Box } from 'components/Box';
import { Flex } from 'components/Flex';
import { Form, LegacyInputField, SubmitButton } from 'components/Form';
import { Body } from 'components/Text/Body';
import { H2 } from 'components/Text/H2';
import { colors, gutterWidth } from 'theme';
import { imageUrl } from 'utils/filestack';
import { getStaticImageSrc } from 'utils/getImageSrc';

const getImageUrl = (handle: string, mimetype: string) =>
  imageUrl({
    handle,
    mimetype,
    transform: `resize=width:2096,fit:max/quality=value:70`,
  });

function StyledInputField({
  name,
  type,
  label,
  disabled,
}: {
  name: string;
  type: string;
  label: string;
  disabled?: boolean;
}) {
  return (
    <LegacyInputField
      name={name}
      type={type}
      label={label}
      inlineLabel
      disabled={disabled}
      // @ts-expect-error TS(2322): Type '{ name: string; type: string; label: string;... Remove this comment to see the full error message
      borderRadius="0"
      border="none"
      height="60px"
      pt="16px"
      pb="10px"
    />
  );
}

const StyledSubmitButton = styled(
  ({ ...props }: ComponentProps<typeof SubmitButton>) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SubmitButton {...props} />
  ),
)`
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;
  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.brandGreen} inset;
    background: ${colors.brandGreen};
  }
`;

type Props = {
  headline: string;
  bgImage: ApiFilestackImageData | null | undefined;
  buttonText: string;
  disabled: boolean;
  isSubmitted: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (...args: Array<any>) => any;
  qaId?: string;
  preForm?: ReactNode;
};

export function CmsEmailCampaignSignup({
  headline,
  bgImage,
  buttonText,
  disabled,
  isSubmitted,
  onSubmit,
  qaId,
  preForm,
}: Props) {
  return (
    <div>
      <Box
        p="80px 20px"
        mx={[-gutterWidth / 2, null, -gutterWidth]}
        width="auto !important"
        borderRadius={[0, 0, 0, 24]}
        background={`url("${
          bgImage
            ? // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
              getImageUrl(bgImage.handle, bgImage.mimetype)
            : getStaticImageSrc('blue-loops.svg')
        }") center/cover`}
      >
        <Box maxWidth="960px" m="0 auto">
          <H2 color="white" textAlign="center">
            {headline}
          </H2>
        </Box>
        <Form
          analyticsTitle="[cms] Email campaign signup block"
          usageContext="page"
          data-qa-id={qaId}
          onSubmit={onSubmit}
          render={() => (
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {isSubmitted ? (
                <Body color="white" textAlign="center" mt="20px">
                  {getText('Thank you for signing up!')}
                </Body>
              ) : (
                <>
                  <Box maxWidth="440px" m="40px auto 0">
                    <Box mb="24px">{preForm}</Box>
                    <Box overflow="hidden" borderRadius="4px">
                      <Box display="flex" flexWrap="wrap">
                        <Box
                          width={['100%', null, '50%', null]}
                          flex="0 0 auto"
                        >
                          <StyledInputField
                            name="firstName"
                            type="text"
                            label={getText('First name')}
                            disabled={disabled}
                          />
                        </Box>
                        <Box
                          width={['100%', null, '50%', null]}
                          flex="0 0 auto"
                          borderLeft={[
                            null,
                            null,
                            `1px solid ${colors.elementGrey}`,
                          ]}
                          borderTop={[
                            `1px solid ${colors.elementGrey}`,
                            null,
                            'none',
                          ]}
                        >
                          <StyledInputField
                            name="lastName"
                            type="text"
                            label={getText('Last name')}
                            disabled={disabled}
                          />
                        </Box>
                      </Box>
                      <Box borderTop={`1px solid ${colors.elementGrey}`}>
                        <StyledInputField
                          name="email"
                          type="email"
                          label={`${getText('Email')}*`}
                          disabled={disabled}
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Flex justifyContent="center" mt="40px">
                    <StyledSubmitButton disabled={disabled}>
                      {buttonText}
                    </StyledSubmitButton>
                  </Flex>
                </>
              )}
            </>
          )}
        />
      </Box>
    </div>
  );
}
